import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Button, AppBar, Toolbar, Typography, Fade } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import PreviousIcon from '@mui/icons-material/ChevronLeft';

import apiClient from 'src/utils/apiClient';
import customTemplateApi from 'src/utils/api/customTemplateApi';
import utils from 'src/utils/utils';

import MessageModelItem from 'src/components/common/MessageModelItem';
import MassActionMsgImportTplItem from './MassActionMsgImportTplItem';
import Loader from 'src/components/common/Loader';
import AccordionItem from 'src/components/common/AccordionItem';

import CardList from 'src/components/common/CardList';
import { useMassActionState } from 'src/components/massAction/MassActionStateContext';
import { CONTENT_EDITOR } from './MassActionMessage';
import { CONTENT_SELECTOR } from '../MassActionEditor';
import { useAlert } from 'src/hooks';
import useApiCaller from 'src/hooks/useApiCaller';
import { useCheckContentFieldsAndSelectedColumns } from 'src/components/massAction/state/massAction';

const useStyles = makeStyles()(() => ({
  appBar: {
    position: 'relative',
    textAlign: 'left',
  },
  flex: { flex: 1 },
}));

const StyledAppBar = ({
  goToStep,
  currentName,
  actionType,
  forceIsNewContent,
  currentMassActionId,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  return (
    <AppBar className={classes.appBar} color="primary">
      <Toolbar style={{ paddingLeft: '8px' }}>
        <div className={classes.flex}>
          <Typography variant="h5" color="inherit">
            {t(`smartmessaging.contentEditor.title.${actionType}`)}
          </Typography>
          <Typography variant="body2" color="inherit">
            {currentName}
            {!!currentMassActionId && ` (${currentMassActionId})`}
          </Typography>
        </div>
        <Button
          style={{ margin: '2px' }}
          variant="text"
          color="primary"
          onClick={e => {
            e.preventDefault();
            forceIsNewContent(false);
            goToStep(CONTENT_SELECTOR);
          }}
        >
          <PreviousIcon color="inherit" />
          {t('smartmessaging.buttonLabel.previous')}
        </Button>
      </Toolbar>
    </AppBar>
  );
};

StyledAppBar.propTypes = {
  goToStep: PropTypes.func.isRequired,
  currentName: PropTypes.string.isRequired,
  actionType: PropTypes.number.isRequired,
  currentMassActionId: PropTypes.number.isRequired,
  forceIsNewContent: PropTypes.func.isRequired,
};

const MessageTemplateSelectorRender = ({
  templates,
  customTemplates,
  goToStep,
  actionType,
  expanded,
  setExpanded,
  loadStoredFileData,
  onFileUpload,
}) => {
  const { t } = useTranslation();

  const [{ currentName, currentMassActionId }, setMassActionState] = useMassActionState();

  const forceIsNewContent = value =>
    setMassActionState(state => ({ ...state, forceIsNewContent: value }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <StyledAppBar
        goToStep={goToStep}
        currentName={currentName}
        actionType={actionType}
        forceIsNewContent={forceIsNewContent}
        currentMassActionId={currentMassActionId}
      />
      <Fade in timeout={500}>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <AccordionItem
            expanded={expanded === 'resagenerics'}
            onChange={(e, value) => {
              setExpanded((value && 'resagenerics') || '');
            }}
            title={t('smartmessaging.contentEditor.selectResaGenericTemplate')}
          >
            <div
              style={{
                flex: 1,
                padding: '8px',
                overflow: 'hidden',
              }}
            >
              {templates.map(messageModel => (
                <MessageModelItem
                  hideLang
                  key={messageModel.id}
                  messageModel={messageModel}
                  onMessageModelSelect={() => {
                    loadStoredFileData(messageModel.storedFileId, messageModel.object);
                  }}
                />
              ))}
            </div>
          </AccordionItem>
          {!!(customTemplates && customTemplates.length) && (
            <AccordionItem
              expanded={expanded === 'custom'}
              onChange={(e, value) => {
                setExpanded((value && 'custom') || '');
              }}
              title={t('smartmessaging.contentEditor.selectGenericCustomTemplate')}
            >
              <CardList
                items={customTemplates}
                onItemClick={(e, item) => {
                  e.preventDefault();
                  loadStoredFileData(item.storedfileId, item.subject || '');
                }}
              />
            </AccordionItem>
          )}
          <AccordionItem
            expanded={expanded === 'imp'}
            onChange={(e, value) => {
              setExpanded((value && 'imp') || '');
            }}
            title={t('smartmessaging.contentEditor.uploadTemplate')}
          >
            <MassActionMsgImportTplItem
              onFileLoaded={data => {
                onFileUpload(data);
              }}
            />
          </AccordionItem>
        </div>
      </Fade>
    </div>
  );
};

MessageTemplateSelectorRender.propTypes = {
  templates: PropTypes.array.isRequired,
  customTemplates: PropTypes.array.isRequired,
  onFileUpload: PropTypes.func.isRequired,
  goToStep: PropTypes.func.isRequired,
  actionType: PropTypes.number.isRequired,
  expanded: PropTypes.string,
  setExpanded: PropTypes.func.isRequired,
  loadStoredFileData: PropTypes.func.isRequired,
};

MessageTemplateSelectorRender.defaultProps = { expanded: null };

function MessageTemplateSelector({ actionType, setStep, goToStep }) {
  const [templates, setTemplates] = useState(null);

  const { showAlert } = useAlert();
  const { t } = useTranslation();
  const [
    {
      requestModel: { id: rmId },
    },
    setMassActionState,
  ] = useMassActionState();
  const callApi = useApiCaller();
  const currentLanguage = useSelector(state => state.app.currentLanguage.shortName);

  const checkContentFieldsAndSelectedColumns = useCheckContentFieldsAndSelectedColumns();

  const updateCurrentCampaignAction = useCallback(
    campaignAction => {
      setMassActionState(state => ({
        ...state,
        currentCampaignAction: campaignAction,
      }));
    },
    [setMassActionState]
  );

  useEffect(() => {
    let fetchData;
    if (!templates) {
      fetchData = async () => {
        const list = await callApi(apiClient.getMessageModels, [rmId, actionType]);
        const customList = await callApi(customTemplateApi.getGenericTemplates, [actionType]);
        setTemplates({
          templates: list || [],
          customTemplates: (customList || []).filter(ct => !!ct.storedfileId && !!ct.actionTypeId),
        });
      };

      fetchData();
    }
  });
  const [expanded, setExpanded] = useState(null);

  const receiveContent = useCallback(
    (content, object) => {
      checkContentFieldsAndSelectedColumns(
        content,
        actionType,
        contentFields => {
          updateCurrentCampaignAction({ language: currentLanguage || 'FR' });
          setStep(CONTENT_EDITOR, {
            content: content || '',
            object: object || '',
            fields: contentFields,
          });
        },
        showAlert
      );
    },
    [
      actionType,
      checkContentFieldsAndSelectedColumns,
      currentLanguage,
      setStep,
      showAlert,
      updateCurrentCampaignAction,
    ]
  );

  const loadStoredFileData = useCallback(
    async (fileId, object) => {
      const content = await callApi(apiClient.get, [utils.link(fileId)]);
      receiveContent(content, object);
    },
    [callApi, receiveContent]
  );

  const onFileUpload = useCallback(
    data => {
      receiveContent(data, '');
    },
    [receiveContent]
  );

  return (
    (!!templates && (
      <MessageTemplateSelectorRender
        templates={templates.templates}
        customTemplates={templates.customTemplates}
        loadStoredFileData={loadStoredFileData}
        onFileUpload={onFileUpload}
        setStep={setStep}
        goToStep={goToStep}
        actionType={actionType}
        expanded={expanded}
        setExpanded={setExpanded}
      />
    )) || <Loader wrapperClass="ghostWrapperClass" text={t('smartmessaging.loading')} />
  );
}

MessageTemplateSelector.propTypes = {
  actionType: PropTypes.number.isRequired,
  setStep: PropTypes.func.isRequired,
  goToStep: PropTypes.func.isRequired,
};

export default MessageTemplateSelector;
